/**
 * Memories API Service
 * Handles all API communication for the Trip Memories feature
 */

import { API_BASE_URL } from '../config';
import imageCompression from 'browser-image-compression';

const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
};

/**
 * Compression options for mobile-friendly uploads
 * Target: 1920px max, ~80% quality, under 500KB
 */
const IMAGE_COMPRESSION_OPTIONS = {
    maxSizeMB: 0.5, // 500KB max
    maxWidthOrHeight: 1920, // Full HD max
    useWebWorker: true,
    fileType: 'image/webp', // Convert to WebP for better compression
    initialQuality: 0.8, // 80% quality
};

/**
 * Compress an image file before upload
 * @param {File} file - The image file to compress
 * @param {function} onProgress - Progress callback (0-100)
 * @returns {Promise<File>} - Compressed file
 */
export const compressImage = async (file, onProgress = null) => {
    // Only compress images, not videos
    if (!file.type.startsWith('image/')) {
        return file;
    }

    try {
        const options = {
            ...IMAGE_COMPRESSION_OPTIONS,
            onProgress: onProgress ? (progress) => onProgress(Math.round(progress)) : undefined,
        };

        const compressedFile = await imageCompression(file, options);
        
        console.log(`Compressed ${file.name}: ${(file.size / 1024).toFixed(0)}KB -> ${(compressedFile.size / 1024).toFixed(0)}KB`);
        
        return compressedFile;
    } catch (error) {
        console.warn('Image compression failed, using original:', error);
        return file;
    }
};

/**
 * Get all trip data including days, memories, notes for a trip
 * @param {number} tripId - The trip ID
 * @returns {Promise<{trip: object, days: array}>}
 */
export const getTripMemoriesData = async (tripId) => {
    const response = await fetch(
        `${API_BASE_URL}/memories.php?action=trip_data&trip_id=${tripId}`,
        { headers: getAuthHeader() }
    );
    
    if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to load trip data' }));
        throw new Error(error.error || 'Failed to load trip data');
    }
    
    return response.json();
};

/**
 * Get memories list for a trip (optionally filtered by day)
 * @param {number} tripId - The trip ID
 * @param {number} [dayId] - Optional day ID to filter by
 * @returns {Promise<{memories: array}>}
 */
export const getMemoriesList = async (tripId, dayId = null) => {
    let url = `${API_BASE_URL}/memories.php?action=list&trip_id=${tripId}`;
    if (dayId) url += `&day_id=${dayId}`;
    
    const response = await fetch(url, { headers: getAuthHeader() });
    
    if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to load memories' }));
        throw new Error(error.error || 'Failed to load memories');
    }
    
    return response.json();
};

/**
 * Get highlights for a trip
 * @param {number} tripId - The trip ID
 * @returns {Promise<{highlights: array}>}
 */
export const getHighlights = async (tripId) => {
    const response = await fetch(
        `${API_BASE_URL}/memories.php?action=highlights&trip_id=${tripId}`,
        { headers: getAuthHeader() }
    );
    
    if (!response.ok) {
        throw new Error('Failed to load highlights');
    }
    
    return response.json();
};

/**
 * Get day note
 * @param {number} tripId - The trip ID
 * @param {number} dayId - The day ID
 * @returns {Promise<{note_text: string, id: number|null}>}
 */
export const getDayNote = async (tripId, dayId) => {
    const response = await fetch(
        `${API_BASE_URL}/memories.php?action=day_note&trip_id=${tripId}&day_id=${dayId}`,
        { headers: getAuthHeader() }
    );
    
    if (!response.ok) {
        throw new Error('Failed to load day note');
    }
    
    return response.json();
};

/**
 * Upload a memory file (with automatic compression for images)
 * @param {number} tripId - The trip ID
 * @param {number} dayId - The day ID
 * @param {File} file - The file to upload
 * @param {object} options - Additional options (activityId, caption, skipCompression)
 * @param {function} onProgress - Progress callback (total progress including compression)
 * @returns {Promise<{success: boolean, id: number, media_path: string}>}
 */
export const uploadMemory = async (tripId, dayId, file, options = {}, onProgress = null) => {
    let fileToUpload = file;
    
    // Compress images before upload (unless skipped)
    if (!options.skipCompression && file.type.startsWith('image/')) {
        if (onProgress) onProgress(0, 'compressing');
        fileToUpload = await compressImage(file, (compressionProgress) => {
            // Compression is 0-30% of total progress
            if (onProgress) onProgress(Math.round(compressionProgress * 0.3), 'compressing');
        });
        if (onProgress) onProgress(30, 'uploading');
    }
    
    const formData = new FormData();
    formData.append('file', fileToUpload);
    if (options.activityId) formData.append('activity_id', options.activityId);
    if (options.caption) formData.append('caption', options.caption);
    
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const isImage = file.type.startsWith('image/');
        
        xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable && onProgress) {
                const uploadPercent = Math.round((event.loaded / event.total) * 100);
                // If image was compressed, upload is 30-100% of total progress
                // If video (no compression), upload is 0-100%
                const totalPercent = isImage && !options.skipCompression 
                    ? 30 + Math.round(uploadPercent * 0.7)
                    : uploadPercent;
                onProgress(totalPercent, 'uploading');
            }
        });
        
        xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    resolve(JSON.parse(xhr.responseText));
                } catch (e) {
                    reject(new Error('Invalid response from server'));
                }
            } else {
                try {
                    const error = JSON.parse(xhr.responseText);
                    reject(new Error(error.error || 'Upload failed'));
                } catch (e) {
                    reject(new Error('Upload failed'));
                }
            }
        });
        
        xhr.addEventListener('error', () => {
            reject(new Error('Network error during upload'));
        });
        
        xhr.open('POST', `${API_BASE_URL}/memories.php?action=upload&trip_id=${tripId}&day_id=${dayId}`);
        
        const token = localStorage.getItem('token');
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        
        xhr.send(formData);
    });
};

/**
 * Save day note
 * @param {number} tripId - The trip ID
 * @param {number} dayId - The day ID
 * @param {string} noteText - The note text
 * @returns {Promise<{success: boolean, note: object}>}
 */
export const saveDayNote = async (tripId, dayId, noteText) => {
    const response = await fetch(
        `${API_BASE_URL}/memories.php?action=save_note&trip_id=${tripId}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeader()
            },
            body: JSON.stringify({ day_id: dayId, note_text: noteText })
        }
    );
    
    if (!response.ok) {
        throw new Error('Failed to save note');
    }
    
    return response.json();
};

/**
 * Update memory caption
 * @param {number} memoryId - The memory ID
 * @param {string} caption - The new caption
 * @returns {Promise<{success: boolean, caption: string}>}
 */
export const updateCaption = async (memoryId, caption) => {
    const response = await fetch(
        `${API_BASE_URL}/memories.php?action=caption&memory_id=${memoryId}`,
        {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeader()
            },
            body: JSON.stringify({ caption })
        }
    );
    
    if (!response.ok) {
        throw new Error('Failed to update caption');
    }
    
    return response.json();
};

/**
 * Toggle memory highlight status
 * @param {number} memoryId - The memory ID
 * @returns {Promise<{success: boolean, is_highlight: boolean}>}
 */
export const toggleHighlight = async (memoryId) => {
    const response = await fetch(
        `${API_BASE_URL}/memories.php?action=highlight&memory_id=${memoryId}`,
        {
            method: 'PATCH',
            headers: getAuthHeader()
        }
    );
    
    if (!response.ok) {
        throw new Error('Failed to toggle highlight');
    }
    
    return response.json();
};

/**
 * Update memory privacy
 * @param {number} memoryId - The memory ID
 * @param {string} privacyLevel - The privacy level (private, trip_members, public)
 * @returns {Promise<{success: boolean, privacy_level: string}>}
 */
export const updatePrivacy = async (memoryId, privacyLevel) => {
    const response = await fetch(
        `${API_BASE_URL}/memories.php?action=privacy&memory_id=${memoryId}`,
        {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeader()
            },
            body: JSON.stringify({ privacy_level: privacyLevel })
        }
    );
    
    if (!response.ok) {
        throw new Error('Failed to update privacy');
    }
    
    return response.json();
};

/**
 * Reorder memories
 * @param {array} memories - Array of {id, sort_order}
 * @returns {Promise<{success: boolean}>}
 */
export const reorderMemories = async (memories) => {
    const response = await fetch(
        `${API_BASE_URL}/memories.php?action=reorder`,
        {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeader()
            },
            body: JSON.stringify({ memories })
        }
    );
    
    if (!response.ok) {
        throw new Error('Failed to reorder memories');
    }
    
    return response.json();
};

/**
 * Reassign memory to different day/activity
 * @param {number} memoryId - The memory ID
 * @param {object} assignment - New assignment {dayId, activityId}
 * @returns {Promise<{success: boolean}>}
 */
export const reassignMemory = async (memoryId, assignment) => {
    const response = await fetch(
        `${API_BASE_URL}/memories.php?action=reassign&memory_id=${memoryId}`,
        {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeader()
            },
            body: JSON.stringify({
                day_id: assignment.dayId,
                activity_id: assignment.activityId
            })
        }
    );
    
    if (!response.ok) {
        throw new Error('Failed to reassign memory');
    }
    
    return response.json();
};

/**
 * Delete a memory (soft delete)
 * @param {number} memoryId - The memory ID
 * @returns {Promise<{success: boolean}>}
 */
export const deleteMemory = async (memoryId) => {
    const response = await fetch(
        `${API_BASE_URL}/memories.php?action=memory&memory_id=${memoryId}`,
        {
            method: 'DELETE',
            headers: getAuthHeader()
        }
    );
    
    if (!response.ok) {
        throw new Error('Failed to delete memory');
    }
    
    return response.json();
};

/**
 * Create a share link for trip memories
 * @param {number} tripId - The trip ID
 * @param {object} options - Share options
 * @returns {Promise<{success: boolean, token: string, share_url: string, expiry_date: string}>}
 */
export const createShareLink = async (tripId, options = {}) => {
    const response = await fetch(
        `${API_BASE_URL}/memories.php?action=create_share_link&trip_id=${tripId}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeader()
            },
            body: JSON.stringify({
                share_type: options.shareType || 'all',
                expiry_days: options.expiryDays || 7,
                allow_download: options.allowDownload || false,
                password: options.password || null,
                selected_memory_ids: options.selectedIds || null
            })
        }
    );
    
    if (!response.ok) {
        throw new Error('Failed to create share link');
    }
    
    return response.json();
};

export default {
    getTripMemoriesData,
    getMemoriesList,
    getHighlights,
    getDayNote,
    uploadMemory,
    saveDayNote,
    updateCaption,
    toggleHighlight,
    updatePrivacy,
    reorderMemories,
    reassignMemory,
    deleteMemory,
    createShareLink,
};
